/* global AjaxHelper */
import { Controller } from 'stimulus'
import './widget_component.css'
import {
  fetchJson,
  loadHtmlInto,
  showErrorTemplate,
} from '~/controllers/help_desks/lib/fetch'

const PANEL_OPEN_CLASS = 'help-desk-widget-open'

export default class extends Controller {
  static targets = [
    'button',
    'panel',
    'panelContent',
    'openIcon',
    'closeIcon',
    'loadErrorTemplate',
  ]
  static values = {
    panelUrl: String,
    open: Boolean,
    loaded: Boolean,
    showTicketId: Number,
  }

  connect() {
    // In admin the widget is carried across navigation as a permanent element, so this runs
    // again on every page change with the panel still open — and disconnect just took the
    // class that makes the frustration widget yield off <html>. Put it back.
    if (this.openValue) document.documentElement.classList.add(PANEL_OPEN_CLASS)

    if (!this.showTicketIdValue) return

    this.showTicketIdValue = 0 // prevent re-trigger on Turbo cache restore
    this.open()
  }

  toggle() {
    this.openValue ? this.close() : this.open()
  }

  // `help-desk:open` from a link elsewhere on the page, e.g. admin's "Send a message". Unlike
  // toggle it never closes the widget, and it can name the tab to land on (`detail.tab`). A panel
  // that is already loaded hears the same event and switches tab itself; until then nothing is
  // listening, so the widget carries the tab — into the fetch, or over to the panel once it lands.
  show(event) {
    const tab = event.detail?.tab
    if (tab && !this.loadedValue) this.requestedTab = tab
    if (!this.openValue) this.open()
  }

  // A page's Lars button. The panel opens the object's chat.
  showObject(event) {
    this.#request('object', event.detail)
  }

  // A page asking Lars, e.g. the bot wizard. The panel starts the chat.
  showAsk(event) {
    this.#request('ask', event.detail)
  }

  #request(name, detail) {
    if (!this.loadedValue) this.requestedEvent = { name, detail }
    if (!this.openValue) this.open()
  }

  open() {
    this.openValue = true
    this.buttonTarget.setAttribute('aria-expanded', 'true')
    this.panelTarget.classList.remove(
      'opacity-0',
      'translate-y-5',
      'scale-50',
      'pointer-events-none'
    )
    this.panelTarget.classList.add(
      'opacity-100',
      'translate-y-0',
      'scale-100',
      'pointer-events-auto'
    )
    this.openIconTarget.classList.add('hidden')
    this.closeIconTarget.classList.remove('hidden')
    // The frustration widget sits in the same corner and yields to us — see its CSS.
    document.documentElement.classList.add(PANEL_OPEN_CLASS)

    // A panel loading for the first time focuses itself as it connects. One that is already
    // loaded has nothing new connecting, so reopening has to ask for the focus.
    if (this.loadedValue) {
      document.dispatchEvent(new CustomEvent('help-desk:focus'))
    } else {
      this.loadPanel()
    }
  }

  close() {
    // Esc fires from anywhere on the page — only take focus back when it is ours to hand over,
    // otherwise closing an already-closed panel would yank the user out of what they're typing in.
    const reclaimFocus = this.panelTarget.contains(document.activeElement)

    this.openValue = false
    this.buttonTarget.setAttribute('aria-expanded', 'false')
    this.panelTarget.classList.add(
      'opacity-0',
      'translate-y-5',
      'scale-50',
      'pointer-events-none'
    )
    this.panelTarget.classList.remove(
      'opacity-100',
      'translate-y-0',
      'scale-100',
      'pointer-events-auto'
    )
    this.openIconTarget.classList.remove('hidden')
    this.closeIconTarget.classList.add('hidden')
    document.documentElement.classList.remove(PANEL_OPEN_CLASS)

    // The panel stays focusable while closed (it fades out rather than unmounting), so focus
    // left inside it would keep taking keystrokes no one can see.
    if (reclaimFocus) this.buttonTarget.focus({ preventScroll: true })
  }

  disconnect() {
    document.documentElement.classList.remove(PANEL_OPEN_CLASS)
  }

  loadPanel() {
    const url = new URL(this.panelUrlValue, window.location.origin)
    if (this.requestedTab) url.searchParams.set('tab', this.requestedTab)
    this.requestedTab = null

    fetchJson(url.toString())
      .then((data) => {
        if (data.replace) {
          AjaxHelper.doUpdate(data)
        } else {
          loadHtmlInto(this.panelContentTarget, data)
        }
        this.loadedValue = true
        // The panel's controller loads lazily; it takes this on connect.
        if (this.requestedEvent) {
          this.panelContentTarget.dataset.hdRequest = JSON.stringify(
            this.requestedEvent
          )
          this.requestedEvent = null
        }
        this._handOverRequestedTab()
      })
      .catch(() =>
        showErrorTemplate(this.panelContentTarget, this.loadErrorTemplateTarget)
      )
  }

  // A tab asked for while the panel was already on its way. The panel's controller connects on
  // the next tick, so it's handed the same event then — unless the widget was closed meanwhile,
  // which the event would undo.
  _handOverRequestedTab() {
    const tab = this.requestedTab
    this.requestedTab = null
    if (!tab) return

    requestAnimationFrame(() => {
      if (!this.openValue) return

      document.dispatchEvent(
        new CustomEvent('help-desk:open', { detail: { tab } })
      )
    })
  }
}
